import 'rc-slider/assets/index.css'
import Big from 'big.js'
import useTranslation from 'next-translate/useTranslation'
import { useContext, useMemo } from 'react'

import calculateBoost from '../utils/rewards/calculateBoost'

import { LockedMETContext } from './LockedMetContext'
import {
  OperationDetailsList,
  OperationDetailsPreviewItem
} from './OperationDetails'

function LockFormDetails({
  amountToLock,
  lockPeriodInSeconds
}: {
  amountToLock: number
  lockPeriodInSeconds: number
}) {
  const { t } = useTranslation()

  const {
    amountLocked,
    esMet = { balance: 0 },
    maximumBoost,
    maximumLockPeriod,
    rewardsData
  } = useContext(LockedMETContext)

  const { balance: esMetBalance = 0 } = esMet

  const boostedAmount =
    amountToLock && maximumLockPeriod
      ? Big(amountToLock)
          .mul(lockPeriodInSeconds)
          .mul(maximumBoost)
          .div(maximumLockPeriod)
      : Big(0)

  const esMetAmount = Big(amountToLock).plus(boostedAmount)
  const nextEsMetBalance = Big(esMetBalance).plus(esMetAmount)

  const nextAmountLocked = Big(amountLocked).plus(amountToLock)

  const votingPower = useMemo(
    () =>
      esMetBalance && amountLocked && Big(amountLocked).gt(0)
        ? Big(esMetBalance).div(amountLocked).toFixed(2)
        : 0,
    [amountLocked, esMetBalance]
  )
  const nextVotingPower = useMemo(
    () =>
      nextEsMetBalance && nextAmountLocked.gt(0)
        ? Big(nextEsMetBalance).div(nextAmountLocked).toFixed(2)
        : votingPower,
    [nextAmountLocked, nextEsMetBalance, votingPower]
  )

  const nextSfBoostPercent = useMemo(
    function () {
      if (Big(rewardsData?.totalBoost || 0).gt(0)) {
        const boost = calculateBoost({
          ...rewardsData,
          esMETBalance: nextEsMetBalance
        })
        return Big(boost).div(rewardsData.totalBoost).times(100)
      }
      return 0
    },
    [nextEsMetBalance, rewardsData]
  )

  return (
    <div className="bg-blue-5 mt-6 rounded-lg px-4 pb-3 pt-4">
      <span className="text-lg font-medium">
        {t('lock-form.details-title')}
      </span>
      <div className="bg-blue-tertiary mt-6 rounded-lg">
        <OperationDetailsList>
          <OperationDetailsPreviewItem
            currentAsset="esmet"
            currentValue={Big(esMetBalance).toFixed(4)}
            nextAsset="esmet"
            nextValue={nextEsMetBalance.toFixed(4)}
            title={t('lock-form.esmet-amount')}
          />
          <OperationDetailsPreviewItem
            currentValue={`${votingPower}x`}
            nextValue={`${nextVotingPower}x`}
            title={t('lock-form.voting-power')}
          />
          {process.env.NEXT_PUBLIC_FLAGS_DISPLAY_REWARDS === 'true' &&
            Big(rewardsData?.totalBoost || 0).gt(0) && (
              <OperationDetailsPreviewItem
                currentValue={`${Big(rewardsData?.rewardsPercent || 0).toFixed(
                  parseInt(process.env.NEXT_PUBLIC_UI_PERCENT_DECIMALS)
                )}%`}
                nextValue={`${Big(nextSfBoostPercent).toFixed(
                  parseInt(process.env.NEXT_PUBLIC_UI_PERCENT_DECIMALS)
                )}%`}
                title={t('lock-form.rewards')}
              />
            )}
        </OperationDetailsList>
      </div>
    </div>
  )
}

export default LockFormDetails
